import { z } from "zod";

import {
  DEFAULT_TIME_DISPLAY_MODE,
  type EventVisibility,
  SETTINGS_VERSION,
  settingsSchema,
  type TimeDisplayMode,
  type WorkspaceSettings,
} from "./schema/v1/v1";

/**
 * `WorkspaceSettings` with every field materialized. Session state holds this
 * shape from seed onward — `initializeVizState` builds it and nothing
 * downstream may drop a field back to absent — so Share serializes a dense
 * config with no fill step and the recipient sees exactly what the sharer saw.
 */
export interface ResolvedWorkspaceSettings {
  version: number;
  events: EventVisibility;
  timeDisplayMode: TimeDisplayMode;
  layoutId: string | null;
}

/**
 * The epoch a config with no `version` belongs to. Fixed at 1 forever: it
 * describes configs minted before the marker existed, so it must not follow
 * {@link SETTINGS_VERSION} upward.
 */
const IMPLICIT_SETTINGS_VERSION = 1;

/**
 * The system defaults, each chosen to match what the code did before settings
 * existed: every event visible, the `Local` clock, no layout reference. An
 * absent field resolves to these, so changing one moves exactly the links that
 * could not have had an opinion — minted before the feature, or before that
 * setting — plus fresh sessions. A link that recorded a setting stays pinned
 * to what it recorded.
 *
 * `version` is the exception: it is a write-side marker, not a preference, and
 * an absent one resolves to {@link IMPLICIT_SETTINGS_VERSION} instead of to the
 * value here. It sits in this object so the object is a complete
 * `ResolvedWorkspaceSettings`, and so `version` counts as a known key when
 * `seedSettingsForRestoredWorkspace` sifts out the unknown ones.
 */
// `satisfies` ties the hand-written interface to the schema-derived shape: a
// field added to `ResolvedWorkspaceSettings` without a `WorkspaceSettings`
// counterpart (or vice versa) fails to compile here, instead of being
// silently stripped later by the seed re-parse.
export const SYSTEM_DEFAULT_SETTINGS: ResolvedWorkspaceSettings = {
  version: SETTINGS_VERSION,
  events: { mode: "all" },
  timeDisplayMode: DEFAULT_TIME_DISPLAY_MODE,
  layoutId: null,
} satisfies Required<WorkspaceSettings>;

/**
 * The recorded block, re-validated. `VizConfig.from_obj` wraps the raw stored
 * object and throws away zod's parse output, so a restored `State` carries
 * whatever the config literally held and the schema's per-field
 * `.catch(undefined)` never reached it. Re-parsing at seed is what makes that
 * documented degradation real: a malformed field lands here as absent. This
 * validation belongs to the restored path alone — `seedSettingsForFreshSession`
 * trusts its input, whose one producer (the loader) parses it through
 * `parseWorkspaceSettingsDefaults` first.
 */
const recordedSettingsSchema = settingsSchema.catch({});

/**
 * Settings for a session opened from a shared workspace. Recorded values
 * govern; an absent or malformed field falls back to the system default, never
 * to the viewer's preference — a link minted before a setting existed must not
 * obey two different rules depending on each setting's release date. That is
 * why the viewer's defaults are not a parameter here.
 */
export function seedSettingsForRestoredWorkspace(
  recorded: WorkspaceSettings | undefined,
): ResolvedWorkspaceSettings {
  const validated = recordedSettingsSchema.parse(recorded);

  // Keys this client does not know — a newer client's settings — ride through
  // untouched, so an open-and-reshare by an older client never deletes them.
  // This carry-through is deliberately here and not in `settingsSchema`:
  // loosening the schema would also let `layoutId` and `version` back through
  // the defaults path's `.omit()`, undoing two deliberate exclusions.
  const unknownKeys =
    recorded !== null &&
    typeof recorded === "object" &&
    !Array.isArray(recorded)
      ? Object.fromEntries(
          Object.entries(recorded).filter(
            ([key]) => !Object.hasOwn(SYSTEM_DEFAULT_SETTINGS, key),
          ),
        )
      : {};

  return {
    ...unknownKeys,
    // The recorded epoch rides through rather than being restamped: a reshare
    // by this client must not relabel a block written under a later epoch as
    // one this client authored. Nothing branches on the value yet — see
    // `settingsSchema.version`.
    version: validated.version ?? IMPLICIT_SETTINGS_VERSION,
    events: validated.events ?? SYSTEM_DEFAULT_SETTINGS.events,
    timeDisplayMode:
      validated.timeDisplayMode ?? SYSTEM_DEFAULT_SETTINGS.timeDisplayMode,
    layoutId: validated.layoutId ?? SYSTEM_DEFAULT_SETTINGS.layoutId,
  };
}

/**
 * The per-user defaults value stored under the `workspace_settings_defaults`
 * config key. It omits two of the settings fields.
 *
 * `layoutId`, because the per-user default layout already exists as
 * `user_default_layout_id` and is consumed by the loader's resolver chain.
 *
 * `version`, because it marks the epoch of a recorded workspace config, while
 * this config key is read only to seed a fresh session and is never resolved
 * against a stored epoch.
 */
const workspaceSettingsDefaultsSchema = settingsSchema
  .omit({ layoutId: true, version: true })
  .catch({});

/**
 * Derived from the schema so the two cannot drift: widening or narrowing the
 * `.omit()` above moves this type with it.
 */
export type WorkspaceSettingsDefaults = z.infer<
  typeof workspaceSettingsDefaultsSchema
>;

/**
 * Parse the raw config-service value into `WorkspaceSettingsDefaults`. The
 * backend stores this key as an opaque dict, so the shape is enforced only
 * here: a malformed field degrades to absent (the field's system default
 * applies) and a value that is not an object at all degrades to no defaults.
 * No stored value can therefore make this throw, and an old row cannot become
 * a read error as the shape evolves.
 */
export function parseWorkspaceSettingsDefaults(
  value: unknown,
): WorkspaceSettingsDefaults {
  return workspaceSettingsDefaultsSchema.parse(value);
}

/**
 * Settings for a session the viewer starts rather than restores. This is the
 * only place per-user defaults apply: each field takes the user's default when
 * set, the system default otherwise. They are applied once, at seed, so
 * editing them later leaves an already-open session alone.
 *
 * A restored v2 workspace comes through here too, because its state is rebuilt
 * from a layout rather than restored whole (`ConfigSource` keeps `type:
 * "layout"` for it). The loader hands it no defaults, so it seeds from system
 * defaults and a restore still never picks up the viewer's preferences.
 */
export function seedSettingsForFreshSession(
  userDefaults: WorkspaceSettingsDefaults | undefined,
): ResolvedWorkspaceSettings {
  return {
    version: SETTINGS_VERSION,
    events: userDefaults?.events ?? SYSTEM_DEFAULT_SETTINGS.events,
    timeDisplayMode:
      userDefaults?.timeDisplayMode ?? SYSTEM_DEFAULT_SETTINGS.timeDisplayMode,
    layoutId: SYSTEM_DEFAULT_SETTINGS.layoutId,
  };
}
